import React, {
  FC,
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { capitalize } from "lodash";
import {
  FinancialApplicationFraudResult,
  FinancialApplicationFraudReason,
} from "../../models/applications/applicationInterfaces";
import { FraudCategoryCard } from "../FraudCategoryCard";
import { formatFraudCategory } from "../../utils/formatFraudCategory";

interface FraudCategoryDisplayProps {
  fraudResultsData?: FinancialApplicationFraudResult[];
  applicantType?: string; // "applicant" or "coapplicant"
  /**
   * The ID Documents card and the panel it opens. Document verification is part of
   * the FraudGuard+ module, so it belongs in this strip rather than in a section of
   * its own — a reviewer should not have to know which report holds which check.
   */
  idDocuments?: {
    /**
     * The provider's score for the reported attempt, passed through unscaled.
     * Assumed 0-100 to match the fraud cards; unconfirmed by the vendor, and
     * sandbox has returned negatives.
     */
    score?: number;
    /** Drives the card's colour, since document verification has no risk level. */
    color: string;
    /** Rendered in the detail area when the card is selected. */
    detail: React.ReactNode;
    /**
     * Reports a verification that is outstanding while `score` still describes an
     * earlier, completed attempt.
     */
    note?: string;
    noteTooltip?: string;
  };
  /**
   * The OFAC card and its panel: the sanctions screen run inside the fraud
   * evaluation, injected per person exactly as ID Documents is. Its result is an
   * outcome, not a score, so it carries a `value` word instead.
   */
  watchlist?: {
    /** "Clear", "Match", "List match", "Failed". */
    value?: string;
    color: string;
    detail: React.ReactNode;
    /** In place of a value the card does not have — "Unavailable". */
    note?: string;
    noteTooltip?: string;
  };
}

/** The synthetic ID of the ID Documents card — not a persisted fraud category. */
export const ID_DOCUMENTS_CATEGORY = "idDocuments";

/** The synthetic ID of the OFAC card. Labelled by `formatFraudCategory`. */
export const WATCHLIST_CATEGORY = "watchlist";

/** Shared shape of the cards the page injects rather than the API persisting. */
interface InjectedCard {
  score?: number;
  value?: string;
  color: string;
  detail: React.ReactNode;
  note?: string;
  noteTooltip?: string;
  scoreLabel: string;
}

// Display order for categories. ID Documents sits second: the fraud score is the
// headline, and the document itself is the next most probative thing. OFAC sits
// last — a separate compliance check rather than one of the risk scores.
const DISPLAY_ORDER = [
  "fraud",
  ID_DOCUMENTS_CATEGORY,
  "identity",
  "device",
  "email",
  "phone",
  "address",
  WATCHLIST_CATEGORY,
];

/** A card's width when there is room for it — the strip's long-standing size. */
const COMFORTABLE_CARD_PX = 130;
/** The narrowest a card may get: FraudCategoryCard's own `minWidth`. */
const MIN_CARD_PX = 110;
const CARD_GAP_PX = 12;
/**
 * The widest a card gets once the strip splits into rows. Balanced rows have
 * few columns, and `1fr` would stretch each card to double its one-row width.
 */
const MAX_WRAPPED_CARD_PX = 160;

/**
 * How many columns the card strip gets, for a strip `width` px wide holding
 * `count` cards.
 *
 * - Fits at the comfortable size: as many columns as fit, filled from the left.
 *   Empty tracks keep their width, so a card is the same size on every tab.
 * - Fits only once cards shrink to their minimum: exactly `count` columns, one
 *   row. With ID Documents, Device and OFAC all present a person can have 8.
 * - Does not fit even then: the fewest rows, balanced — 7 cards are 4 + 3 and
 *   8 are 4 + 4, never a row of 6 with one or two stragglers beneath it.
 */
export const stripColumns = (width: number, count: number): number => {
  const fit = (cardPx: number) =>
    Math.max(1, Math.floor((width + CARD_GAP_PX) / (cardPx + CARD_GAP_PX)));
  const comfortable = fit(COMFORTABLE_CARD_PX);
  if (count <= comfortable) return comfortable;
  const tightest = fit(MIN_CARD_PX);
  if (count <= tightest) return count;
  return Math.ceil(count / Math.ceil(count / tightest));
};

// Categories whose rows are recorded against the application rather than a person,
// and which therefore show on the applicant's tab alone. ID Documents is NOT one of
// these any more: the card now reports the selected person's own attempts, injected
// per person by the page.
const APPLICATION_SCOPED_CATEGORIES = ["device"];

// Risk level colors for reason badges
const getRiskBadgeStyle = (riskLevel?: string) => {
  if (riskLevel === "risk") {
    return {
      backgroundColor: "#fdd5d6",
      color: "#eb2835",
    };
  }
  return {
    backgroundColor: "#eceff1",
    color: "#949da5",
  };
};

// Sort fraud reasons: risk first, then info; alphabetical within each
const sortFraudReasons = (
  reasons: FinancialApplicationFraudReason[]
): FinancialApplicationFraudReason[] => {
  return [...reasons].sort((a, b) => {
    // Risk comes before info
    if (a.riskLevel === "risk" && b.riskLevel !== "risk") return -1;
    if (a.riskLevel !== "risk" && b.riskLevel === "risk") return 1;
    // Alphabetical by message within same risk level
    return (a.message || "").localeCompare(b.message || "");
  });
};

// Get the appropriate scope based on applicant type
/**
 * The persisted fraud-result scope a person tab maps onto, or null for a person
 * who cannot have person-scoped fraud rows.
 *
 * The null case is load-bearing. Tabs used to be only "applicant" and
 * "coapplicant", so collapsing everything else onto "co-applicant" was
 * harmless. Person tabs now also carry a financial person UUID for relationship
 * persons (authorized signers, beneficial owners), and a UUID falling through to
 * "co-applicant" showed the CO-APPLICANT's fraud cards under a signer's name —
 * one person's fraud findings attributed to another. Fraud results are only ever
 * recorded against the applicant, the co-applicant, or the application, so a
 * relationship person legitimately has none; ID Documents is their only card.
 */
export const getScope = (
  applicantType: string
): "applicant" | "co-applicant" | null => {
  if (applicantType === "applicant") return "applicant";
  if (applicantType === "coapplicant" || applicantType === "co-applicant") {
    return "co-applicant";
  }
  return null;
};

/**
 * Display order, defined once. Two copies of this comparator had to stay
 * identical or the tab-existence check and the rendered strip would disagree
 * about order — and an unknown category sorts to -1, i.e. ahead of the fraud
 * headline, so a drifting copy is not a cosmetic difference.
 */
const orderIndex = (category?: string) => {
  const index = DISPLAY_ORDER.indexOf(category || "");
  // Unknown sorts LAST. `indexOf` answers -1, which put any category this build
  // does not recognize AHEAD of the fraud headline card. That is reachable during
  // a cross-repo deploy window: the synthetic/kyc -> identity rename lands in
  // acquire-api and in this app on independent deploys, and categories are
  // synthesized per request, so whichever ships first serves the other a name it
  // has never heard of. Sorting unknowns to the end makes both skew directions
  // merely unfamiliar rather than misordered.
  return index === -1 ? Number.POSITIVE_INFINITY : index;
};

const byDisplayOrder = (
  a: FinancialApplicationFraudResult,
  b: FinancialApplicationFraudResult
) => orderIndex(a.category) - orderIndex(b.category);

/**
 * The fraud cards one person's tab shows, in display order.
 *
 * Exported because the page needs the same answer to decide whether that person
 * gets a tab at all — a tab leading to an empty strip is worse than no tab. Two
 * copies of this filter would drift, and the symptom would be a tab that opens
 * onto nothing.
 */
export const fraudCardsForPerson = (
  fraudResultsData: FinancialApplicationFraudResult[] | undefined,
  applicantType: string
): FinancialApplicationFraudResult[] => {
  if (!fraudResultsData || !Array.isArray(fraudResultsData)) return [];

  const scope = getScope(applicantType);
  // A relationship person has no person-scoped fraud rows, and must not inherit
  // another person's. Returning early rather than filtering on null keeps the
  // application-scoped branch below from leaking Device onto their tab too.
  if (!scope) return [];

  return fraudResultsData
    .filter((result) => {
      // Defensive check for malformed data
      if (!result || typeof result !== "object") return false;
      // Application-scoped categories belong to the APPLICANT's tab only. Device
      // is the clearest case: it profiles the device the application was
      // submitted from, which is the applicant's — showing it under an authorized
      // signer would attribute someone else's hardware to them.
      if (APPLICATION_SCOPED_CATEGORIES.includes(result.category || "")) {
        return result.scope === "application" && applicantType === "applicant";
      }
      return result.scope === scope;
    })
    .sort(byDisplayOrder);
};

export const FraudCategoryDisplay: FC<FraudCategoryDisplayProps> = ({
  fraudResultsData,
  applicantType = "applicant",
  idDocuments,
  watchlist,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Filter and sort fraud results for the current applicant
  const filteredResults = useMemo(
    () => fraudCardsForPerson(fraudResultsData, applicantType),
    [fraudResultsData, applicantType]
  );

  // Injected cards — ID Documents, OFAC — keyed by their synthetic category.
  // The document score is the provider's own, on a scale Socure has not
  // confirmed, so it is not captioned as a risk score; OFAC has no score at all.
  const injected = useMemo(() => {
    // A Map, not an object: a category string is looked up here, and an
    // object would answer "constructor" with a prototype method.
    const byCategory = new Map<string, InjectedCard>();
    if (idDocuments) {
      byCategory.set(ID_DOCUMENTS_CATEGORY, {
        ...idDocuments,
        scoreLabel: "score",
      });
    }
    if (watchlist) {
      byCategory.set(WATCHLIST_CATEGORY, {
        ...watchlist,
        scoreLabel: "result",
      });
    }
    return byCategory;
  }, [idDocuments, watchlist]);

  // Injected cards ride in the same list as the fraud categories so that
  // ordering, selection and keyboard handling need no special case. They are not
  // persisted fraud results, hence the synthetic ids.
  const cards = useMemo(() => {
    const extra = Array.from(injected.entries()).map(
      ([category, card]): FinancialApplicationFraudResult => ({
        id: category,
        scope: "application",
        category,
        riskScore: card.score,
      })
    );
    if (!extra.length) return filteredResults;
    return [...filteredResults, ...extra].sort(byDisplayOrder);
  }, [filteredResults, injected]);

  // The strip's own content width, for `stripColumns`. Null until measured (and
  // where ResizeObserver is missing, e.g. jsdom), when the plain auto-fill grid
  // below stands in.
  const stripRef = useRef<HTMLDivElement>(null);
  const [stripWidth, setStripWidth] = useState<number | null>(null);
  useEffect(() => {
    const node = stripRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    // Width only, and a frame later. Changing the column count changes the
    // strip's HEIGHT, which re-notifies the observer within the same frame —
    // "ResizeObserver loop completed with undelivered notifications". Rounding
    // and comparing drops the height-only notifications; the frame breaks the
    // loop for the rest.
    let frame = 0;
    const observer = new ResizeObserver(([entry]) => {
      const width = Math.round(entry.contentRect.width);
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() =>
        setStripWidth((previous) => (previous === width ? previous : width))
      );
    });
    observer.observe(node);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  const stripTemplate = useMemo(() => {
    if (stripWidth === null) {
      return `repeat(auto-fill, minmax(${COMFORTABLE_CARD_PX}px, 1fr))`;
    }
    const columns = stripColumns(stripWidth, cards.length);
    // Fewer columns than cards means rows; cap the cards so they stay the size
    // they are everywhere else.
    const track = columns < cards.length ? `${MAX_WRAPPED_CARD_PX}px` : "1fr";
    return `repeat(${columns}, minmax(0, ${track}))`;
  }, [stripWidth, cards.length]);

  // Track first category ID to avoid dependency on filteredResults array reference
  const firstCategoryId = cards[0]?.category || null;

  // Auto-select the first card when the data loads OR the person changes.
  //
  // `applicantType` is in the deps deliberately. The effect used to fire only when
  // the first card's id changed, and both tabs normally start with "fraud" — so
  // switching person kept whatever was selected. That was survivable while every
  // card existed on every tab; now that Device is applicant-only, selecting Device
  // and switching to another person left `selectedCategory: "device"` matching no
  // card there: nothing highlighted and an empty detail area, which reads as
  // broken until the reviewer clicks something.
  useEffect(() => {
    setSelectedCategory(firstCategoryId);
  }, [firstCategoryId, applicantType]);

  // Memoized click handler to prevent unnecessary re-renders
  const handleCardClick = useCallback((category: string | undefined) => {
    setSelectedCategory((prev) =>
      prev === category ? null : category || null
    );
  }, []);

  // Get the selected fraud result
  const selectedResult = useMemo(() => {
    if (!selectedCategory) return null;
    return filteredResults.find((r) => r.category === selectedCategory);
  }, [filteredResults, selectedCategory]);

  const selectedInjected = selectedCategory
    ? injected.get(selectedCategory)
    : undefined;

  // Get sorted fraud reasons for the selected category
  const sortedReasons = useMemo(() => {
    if (
      !selectedResult?.fraudReasons ||
      !Array.isArray(selectedResult.fraudReasons)
    ) {
      return [];
    }
    return sortFraudReasons(selectedResult.fraudReasons);
  }, [selectedResult]);

  if (!cards.length) {
    return null;
  }

  return (
    <div>
      {/* Cards Row */}
      {/* A grid rather than a flex row: the empty tracks keep their width when
          the selected person has fewer cards, so a card is the same size on every
          tab. A flex row with `flex: 1` stretched two cards across the whole
          strip, which made the same card look like a different component
          depending on who was selected. The column count comes from
          `stripColumns`, which also keeps a full strip from wrapping one or two
          cards onto a row of their own. */}
      <div
        ref={stripRef}
        className="p-3"
        style={{
          display: "grid",
          gridTemplateColumns: stripTemplate,
          gap: `${CARD_GAP_PX}px`,
          alignItems: "start",
        }}
      >
        {cards.map((result, index) => {
          const card = injected.get(result.category || "");
          return (
            <FraudCategoryCard
              key={`${result.scope || "unknown"}-${
                result.category || "unknown"
              }-${result.id || index}`}
              category={result.category || "Unknown"}
              riskScore={result.riskScore}
              riskLevel={result.riskLevel}
              isSelected={selectedCategory === result.category}
              onClick={() => handleCardClick(result.category)}
              scoreLabel={card?.scoreLabel}
              scoreColorOverride={card?.color}
              note={card?.note}
              noteTooltip={card?.noteTooltip}
              value={card?.value}
            />
          );
        })}
      </div>

      {/* Fraud Reasons Section - aria-live announces changes to screen readers */}
      <div aria-live="polite" aria-atomic="true">
        {/* An injected card's panel occupies the detail area exactly as a fraud
            category's reasons do — same slot, same selection model. */}
        {selectedInjected && (
          <div className="mx-3 mb-3">{selectedInjected.detail}</div>
        )}

        {!selectedInjected && selectedResult && sortedReasons.length > 0 && (
          <div
            className="mx-3 mb-3 p-3 bg-white"
            style={{
              border: "1px solid #ededed",
              borderRadius: "8px",
            }}
          >
            <div>
              {sortedReasons.map((reason) => {
                const badgeStyle = getRiskBadgeStyle(reason.riskLevel);
                return (
                  <div
                    key={reason.id}
                    className="d-flex align-items-center"
                    style={{ padding: "8px 0" }}
                  >
                    <div
                      className="px-2 text-center"
                      style={{
                        ...badgeStyle,
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: 500,
                        minWidth: "50px",
                        marginRight: "12px",
                      }}
                    >
                      {capitalize(reason.riskLevel)}
                    </div>
                    <div style={{ color: "#666" }}>
                      {reason.message}
                      {reason.code && (
                        <span
                          style={{
                            color: "#999",
                            fontSize: "12px",
                            marginLeft: "8px",
                          }}
                        >
                          [{reason.code}]
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Show message when card is selected but no reasons. Never for an
            injected card — its detail is a panel, not a reason list. */}
        {!selectedInjected && selectedResult && sortedReasons.length === 0 && (
          <div
            className="mx-3 mb-3 p-3 bg-white text-center"
            style={{
              border: "1px solid #ededed",
              borderRadius: "8px",
              color: "#999",
            }}
          >
            No details available for{" "}
            {formatFraudCategory(selectedResult.category || "")}.
          </div>
        )}
      </div>
    </div>
  );
};

export default FraudCategoryDisplay;
